import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { normalizeJobStatus } from "@/lib/jobStatus";
import { FailureChip } from "@/components/FailureChip";
import type { FailureFields } from "@/lib/failureClass";
import { PeakRss } from "@/components/MemoryPanel";
import { CacheChip } from "@/components/CacheChip";
import type { CacheFields } from "@/lib/cacheVerdict";
import type { MemSummaryFields } from "@/lib/memTelemetry";
import {
  buildKindOf,
  fmtDurationMs,
  platformLabel,
  sequenceText,
  type JobBuildFields,
} from "@/lib/buildLabels";

type SiblingJob = JobBuildFields &
  MemSummaryFields &
  CacheFields & {
    id: string;
    status: "queued" | "running" | "succeeded" | "failed" | "cancelled";
    created_at: number;
    started_at: number | null;
    finished_at: number | null;
  } & FailureFields;

const STATUS_VARIANT: Record<
  SiblingJob["status"],
  "secondary" | "destructive" | "success" | "warning"
> = {
  queued: "secondary",
  running: "warning",
  succeeded: "success",
  failed: "destructive",
  cancelled: "secondary",
};

/** Loads every build of the run `buildRun` belongs to (admin jobs list,
 *  `build_run=`, in sequence order). Polls while any is unfinished. */
export function useBuildSiblings(
  token: string,
  buildRun: string | null | undefined,
): SiblingJob[] | null {
  const [jobs, setJobs] = useState<SiblingJob[] | null>(null);
  useEffect(() => {
    setJobs(null);
    if (!buildRun) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const load = () => {
      api<{ ok: true; jobs: SiblingJob[] }>(
        token,
        `/admin/jobs?build_run=${encodeURIComponent(buildRun)}&limit=50`,
      )
        .then((r) => {
          if (cancelled) return;
          setJobs(r.jobs.map(normalizeJobStatus));
          if (
            r.jobs.some((j) => j.status === "queued" || j.status === "running")
          )
            timer = setTimeout(load, 5_000);
        })
        .catch(() => {
          if (!cancelled) setJobs((j) => j ?? []);
        });
    };
    load();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [token, buildRun]);
  return jobs;
}

/** The other compile jobs of this job's publish run: position, name,
 *  platform, status and compile time, each a link to its own page. */
export function BuildSiblings({
  jobs,
  currentId,
}: {
  jobs: SiblingJob[];
  currentId: string;
}) {
  if (jobs.length < 2) return null;
  return (
    <div className="space-y-1">
      <div className="text-sm text-muted-foreground">
        Builds of this publish run ({jobs.length} compile jobs; the local
        training runs between them have no job page)
      </div>
      <ul className="space-y-1">
        {jobs.map((j) => {
          const info = buildKindOf(j);
          const current = j.id === currentId;
          return (
            <li
              key={j.id}
              className="flex flex-wrap items-center gap-2 text-sm"
            >
              <span className="tabular-nums w-10 text-muted-foreground">
                {sequenceText(j.build_seq, jobs.length) ?? "—"}
              </span>
              {current ? (
                <span className="font-medium">
                  {info?.label ?? "this job"} (this job)
                </span>
              ) : (
                <Link
                  to={`/logs/${encodeURIComponent(j.id)}`}
                  className="text-primary hover:underline"
                >
                  {info?.label ?? j.id.slice(0, 8)}
                </Link>
              )}
              <Badge variant="outline" className="font-mono font-normal">
                {platformLabel(j)}
              </Badge>
              <Badge variant={STATUS_VARIANT[j.status]}>{j.status}</Badge>
              <FailureChip job={j} cause={false} />
              <span className="text-xs text-muted-foreground tabular-nums">
                {j.status === "queued"
                  ? "queued"
                  : (fmtDurationMs(
                      j.started_at ?? j.created_at,
                      j.finished_at,
                    ) ?? "")}
              </span>
              <PeakRss job={j} />
              <CacheChip job={j} />
            </li>
          );
        })}
      </ul>
    </div>
  );
}
